import React from 'react';
import styles from './Card.module.scss';
import clsx from 'clsx';
import { LinkType, TypesOfCard } from 'pages/authorized/welcome/Welcome';
import { assertNever } from 'helpers/assert-never';
import { AppLink } from './buttons/AppLink';
import { ActionText } from './ActionText.tsx';
import { useScopedTranslation } from '../../application/localization/useScopedTranslation.ts';
import { UserRole } from '../../services/api/api-client.types.ts';
import { QueryFactory } from '../../services/api';
import { tDynamic } from '../../helpers/tDynamic.ts';

type OwnProps = {
  links: LinkType[];
  typeOfCard: TypesOfCard;
  title?: string;
  children?: React.ReactNode;
  smallCard?: boolean;
  additionalLink?: React.ReactNode;
};

export const Card = (props: OwnProps) => {
  const i18n = useScopedTranslation('Page.WelcomePage');
  const userQuery = QueryFactory.UserQuery.useGetCurrentUserInfoQuery();
  const userRoles = userQuery.data?.claimRoles ?? [];

  const accessibleLinks = userRoles.includes(UserRole.Admin)
    ? props.links
    : props.links.filter((link) => {
        if (!link.allowedRoles || link.allowedRoles.length === 0) {
          return true;
        }

        return link.allowedRoles.some((role) => userRoles.includes(role));
      });

  if (accessibleLinks.length === 0) {
    return null;
  }

  let typeStyle = '';

  if (props.typeOfCard === TypesOfCard.Download) {
    typeStyle = styles.downloadCard;
  } else if (props.typeOfCard === TypesOfCard.SelfService) {
    typeStyle = styles.selfServiceCard;
  } else if (props.typeOfCard === TypesOfCard.Information) {
    typeStyle = styles.informationCard;
  } else {
    assertNever(props.typeOfCard as never);
  }

  if (props.smallCard) {
    return (
      <>
        {accessibleLinks.map((link) => (
          <AppLink
            key={link.url}
            className={clsx(
              styles.card,
              typeStyle,
              styles.singleLinkCard,
              styles.flexEnd,
            )}
            to={link.url}
          >
            <ActionText
              className={styles.link}
              text={tDynamic(i18n, link.name)}
            />
          </AppLink>
        ))}
      </>
    );
  }

  return (
    <div className={clsx(styles.card, typeStyle)}>
      <div className={clsx(styles.rowContainer, styles.fullHeight)}>
        <div className={clsx(styles.columnContainer, styles.fullHeight)}>
          {props.title && <div className={styles.cardTitle}>{props.title}</div>}
          <div className={styles.columnContainer}>
            {accessibleLinks.map((link, index) => (
              <AppLink
                key={index}
                className={clsx(
                  styles.linkContainer,
                  index === accessibleLinks.length - 1 && styles.noMarginBottom,
                )}
                to={link.url}
              >
                <ActionText
                  className={styles.link}
                  text={tDynamic(i18n, link.name)}
                />
              </AppLink>
            ))}
            {!!props.additionalLink && props.additionalLink}
          </div>
        </div>
        {!!props.children && (
          <div className={styles.image}>{props.children}</div>
        )}
      </div>
    </div>
  );
};
